/**
 * What each integration does, as its details dialog on /integrations says it: one sentence, then the facts as detail
 * rows. Approved on the prototype (docs/prototypes/integrations-modal/, Emily 2026-10-30; decisions in
 * user_decisions_log). Every fact is the help corpus's (packages/server/src/services/help-agent/knowledge.md,
 * "Connecting Figma", "Exporting issues", "Dashboard"): change one there and here together.
 */
export type IntegrationKey =
  | 'figma' | 'jira' | 'linear' | 'notion' | 'asana' | 'clickup' | 'github' | 'basecamp' | 'monday' | 'trello' | 'slack';

/** A label and its value; a list stacks under its label as bullets (DetailRow `list`). */
export type IntegrationFact = readonly [label: string, value: string | readonly string[]];

const SENDS = ['Screenshots', 'Description', 'Element details and CSS', 'AI analysis', 'Comments'] as const;

export const INTEGRATION_SUMMARY: Record<IntegrationKey, string> = {
  figma: 'Compares pages with their Figma frames and shows linked designs on issues.',
  jira: 'Exports issues to Jira and keeps them in sync both ways.',
  linear: 'Exports issues to Linear and keeps them in sync both ways.',
  notion: 'Exports issues to a Notion database and keeps them in sync both ways.',
  asana: 'Exports issues to Asana and keeps them in sync both ways.',
  trello: 'Exports issues to Trello and keeps them in sync both ways.',
  clickup: 'Exports issues to ClickUp and keeps them in sync both ways.',
  github: 'Exports issues to GitHub and keeps them in sync both ways.',
  basecamp: 'Exports issues as Basecamp to-dos and keeps their status in sync.',
  monday: 'Exports issues to monday.com and keeps them in sync.',
  slack: 'Posts to a Slack channel when issues change.',
};

export const INTEGRATION_FACTS: Record<IntegrationKey, readonly IntegrationFact[]> = {
  figma: [
    ['Used for', 'Visual Comparison frames, files linked to projects, and the linked design on dashboard issue pages'],
    ['Comments', 'Comments on a linked file that are attached to an issue appear on that issue'],
    ['Covers', 'The extension and the dashboard, from one connection'],
  ],
  jira: [
    ['Filed in', 'The Jira project you pick at each export'],
    ['Sends', [...SENDS, 'Attached files']],
    ['Syncs back', ['Title, status, priority and assignee, both ways', 'Comments made in Jira']],
  ],
  linear: [
    ['Filed in', 'The Linear team you pick at each export'],
    ['Sends', [...SENDS, 'Attached files']],
    ['Syncs back', ['Title, status, priority and assignee, both ways', 'Comments made in Linear']],
  ],
  notion: [
    ['Filed in', 'The database chosen when you connect'],
    ['Sends', [...SENDS, 'Attached files link back to the issue']],
    ['Syncs back', ['Page title, Status and Severity, both ways', 'Assignee, when the database has one', 'Comments stay in Notion']],
  ],
  asana: [
    ['Filed in', 'The Asana workspace and project you pick at each export'],
    ['Sends', [...SENDS, 'Attached files', 'A link back to the issue']],
    ['Syncs back', ['Title, status and assignee, both ways', 'Comments made in Asana']],
  ],
  trello: [
    ['Filed in', 'The Trello board and list you pick at each export'],
    ['Sends', [...SENDS, 'Attached files']],
    ['Syncs back', ['Title, status and assignee, both ways', 'Comments made in Trello']],
  ],
  clickup: [
    ['Filed in', 'The ClickUp workspace and list you pick at each export'],
    ['Sends', [...SENDS, 'Attached files', 'A link back to the issue']],
    ['Syncs back', ['Title, status, priority and assignee, both ways', 'Comments made in ClickUp']],
  ],
  github: [
    ['Filed in', 'The repository you pick at each export'],
    ['Sends', [...SENDS, 'A link back to the issue', 'Attached files link back instead']],
    ['Syncs back', ['Title, status and assignee, both ways', 'Comments made in GitHub']],
  ],
  basecamp: [
    ['Filed in', 'The Basecamp project and to-do list you pick at each export'],
    ['Sends', ['A to-do per issue', 'Screenshots and files, inline', 'A link back to the issue']],
    ['Syncs back', ['Completing the to-do marks the issue Resolved', 'Reopening it marks the issue Open', 'Trashing or archiving it marks the issue Closed', 'Comments on the to-do']],
  ],
  monday: [
    ['Filed in', 'The monday.com board and group you pick at each export'],
    ['Sends', ['An item per issue, its first update holding the description', 'Screenshots and files, on that update', 'A link back to the issue']],
    ['Syncs back', ['Status and Priority labels update the issue', 'Archiving or deleting the item closes the issue', 'Updates come back with their author', 'Assignees do not sync']],
    ['Needs', 'An account admin for the first connection from a monday.com account'],
  ],
  slack: [
    ['Posts', 'A message when an issue is created, assigned or commented on'],
    ['Setup', 'Invite the OverlayQA bot to the channel, choose the channel here, then send a test message'],
  ],
};

/** The disconnect step's sentence: what stops, and what stays where it is. */
export function disconnectReason(key: IntegrationKey, name: string): string {
  if (key === 'slack') return 'Slack messages about issues stop. Nothing already posted in Slack is removed.';
  if (key === 'figma') return 'Design comparisons and Figma comment sync stop until you connect again.';
  return `New exports to ${name} stop and changes made in ${name} no longer sync back. Issues already in ${name} stay there.`;
}
